import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import VisitsService from "../../services/VisitsService";
import DropDownService from "../../services/DropDownService";
import FormInputs from "../../components/UI/FormInputs";
import withReduxState from "../../hoc/wReduxState";
import WithRoutify from "../../hoc/WithRoutify";
import Buttons from "../../components/UI/Buttons";
import Acl from "../../hoc/Acl";
import PERMISSIONS from "../../utils/AclPermissions";
import { acl_check } from "../../utils/aclUtils";
import GlobalSearch from "../../components/Layouts/NewAppLayout/GlobalSearch/GlobalSearch";
import { useNavigate } from 'react-router-dom';
import DashboardCounts from './components/DashboardCounts';
import DashboardCountsFullScreen from './components/DashboardCountsFullScreen';
import MissingClaimNumbersFullScreen from './components/MissingClaimNumbersFullScreen';
import WorkRestrictions from './components/WorkRestrictions';
import Visits from './components/Visits';
import EmployeeComplianceMonitor from './components/EmployeeComplianceMonitor';
import OpenInvoices from './components/OpenInvoices';

const DashboardContainer = (props) => {
  const navigate = useNavigate();
  const [dashboardData, setDashBoardData] = useState({});
  const [companyOptions, setCompanyOption] = useState([
    { label: "All Company", value: "" },
  ]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const hasInvoicePermission = acl_check(
    props.profile.permissions,
    PERMISSIONS.INVOICE.LIST
  );
  const hasVisitPermission = acl_check(
    props.profile.permissions,
    PERMISSIONS.VISITS.LIST
  );
  console.log("hasInvoicePermission", hasInvoicePermission);
  const resultClass = "col-md-6 col-6 py-2";
  
  const [isLoading, setLoading] = useState(false);
  const [dashboardCountsLoading, setDashboardCountsLoading] = useState(false);
  const [dashboardCounts, setDashboardCounts] = useState({});
  const [employeeComplianceLoading, setEmployeeComplianceLoading] = useState(false);
  const [workRestrictionsLoading, setWorkRestrictionsLoading] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [filter, setFilter] = useState({ last_updated_by: "" });
  const [physicalsFullscreenOpen, setPhysicalsFullscreenOpen] = useState(false);
  const [drugScreenFullscreenOpen, setDrugScreenFullscreenOpen] = useState(false);
  const [vaccinesFullscreenOpen, setVaccinesFullscreenOpen] = useState(false);
  const [missingClaimNumbersFullscreenOpen, setMissingClaimNumbersFullscreenOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({ from_date: "" }, { to_date: "" });
  const filterDropdownRef = useRef(null);

  // Get globalFilter from props (available through withReduxState HOC)
  const globalFilter = props.globalFilter || {};

  // Extract filters from globalFilter if present - exactly like NewDashboardContainer.js
  // Dashboard uses Dashboard filter (as per getActiveMenuByPath in GlobalSearch)
  // Extract from Visits, Appointments, and Dashboard filters
  const dashboardFilters = useMemo(() => {
    // Merge filters from all relevant keys (Visits, Appointments, Dashboard)
    const relevantKeys = ["Visits", "Appointments", "Dashboard"];
    let mergedFilters = {};
    relevantKeys.forEach((key) => {
      if (globalFilter[key] && typeof globalFilter[key] === "object") {
        mergedFilters = { ...mergedFilters, ...globalFilter[key] };
      }
    });
    return mergedFilters;
  }, [globalFilter]);

  const prevGlobalFiltersRef = useRef(JSON.stringify(dashboardFilters));

  useEffect(() => {
    fetchDashBoard();
    fetchClinic();
  }, []);

  // Watch for globalFilter changes and refetch dashboard data
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(dashboardFilters);
    const prevFiltersStr = prevGlobalFiltersRef.current;

    // Skip refetch on initial mount (when prevFiltersStr matches current)
    if (prevFiltersStr === currentFiltersStr) {
      return;
    }

    // Refetch when globalFilter changes (including when filters are cleared)
    fetchDashBoard();
    prevGlobalFiltersRef.current = currentFiltersStr;
  }, [dashboardFilters, globalFilter]);

  useEffect(() => {
    fetchCompany();
  }, [filter.clinic_id]);

  const fetchDashBoard = async (param = filter, isRefresh = false) => {
    setLoading(true);
    
    // Set all table loaders to true immediately
    setDashboardCountsLoading(true);
    setEmployeeComplianceLoading(true);
    setWorkRestrictionsLoading(true);
    setVisitsLoading(true);
    
    // Start with local filters
    let payload = {};
    if (isRefresh) {
       payload = {
        ...param,
        from_date: "",
        to_date: "",
      };
    } else {
       payload = {
        ...param,
        from_date: dateFilter.from_date,
        to_date: dateFilter.to_date,
      };
    }

    // Global filters override everything - exactly like NewDashboardContainer.js
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      // Global filters completely override local filters
      payload = {
        ...dashboardFilters,
      };
      
      // Preserve date filters from local if not in global filters
      if (!dashboardFilters.from_date && dateFilter.from_date) {
        payload.from_date = dateFilter.from_date;
      }
      if (!dashboardFilters.to_date && dateFilter.to_date) {
        payload.to_date = dateFilter.to_date;
      }
    }

    // Handle clinic_id/company_id relationship
    if (payload.clinic_id === "") {
      payload.company_id = "";
    }
    
    // Fetch main dashboard data
    // const response = await VisitsService.getDashBoard(payload);
    // setDashBoardData(response);
    // setLoading(false);
    
    // Fetch dashboard counts data independently
    VisitsService.getDashboardCounts(payload)
      .then((countsResponse) => {
        if (countsResponse) {
          setDashboardCounts(countsResponse);
        }
        setDashboardCountsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching dashboard counts:', error);
        setDashboardCounts({});
        setDashboardCountsLoading(false);
      });
    
    // Fetch employee compliance monitor data independently with limit 5 initially
    const compliancePayload = {
      ...payload,
      sort_column: 'due_date',
      sort_direction: 'desc'
    };
    if (!compliancePayload.compliance_fullscreen) {
      compliancePayload.limit = 5;
    }
    VisitsService.getEmployeeComplianceMonitor(compliancePayload)
      .then((complianceResponse) => {
        if (complianceResponse && complianceResponse.employee_compliance) {
          setDashBoardData((prev) => ({
            ...prev,
            employee_compliance: complianceResponse.employee_compliance
          }));
        }
        setEmployeeComplianceLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching employee compliance monitor:', error);
        setDashBoardData((prev) => ({
          ...prev,
          employee_compliance: []
        }));
        setEmployeeComplianceLoading(false);
      });
    
    // Fetch work restrictions data independently with limit 5 initially
    const workRestrictionsPayload = {
      ...payload,
      sort_column: 'visit_date',
      sort_direction: 'desc'
    };
    if (!workRestrictionsPayload.work_restrictions_fullscreen) {
      workRestrictionsPayload.limit = 5;
    }
    VisitsService.getWorkRestrictions(workRestrictionsPayload)
      .then((workRestrictionsResponse) => {
        if (workRestrictionsResponse && workRestrictionsResponse.work_restrictions) {
          setDashBoardData((prev) => ({
            ...prev,
            work_restrictions: workRestrictionsResponse.work_restrictions
          }));
        }
        setWorkRestrictionsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching work restrictions:', error);
        setDashBoardData((prev) => ({
          ...prev,
          work_restrictions: []
        }));
        setWorkRestrictionsLoading(false);
      });
    
    // Fetch visits data: use same payload logic as refetchVisits so small card
    // gets the same data as fullscreen (no date filter, limit 5 for card).
    let visitsPayload = {};
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      visitsPayload = { ...dashboardFilters };
    } else if (filter) {
      visitsPayload = { ...filter };
    }
    if (visitsPayload.clinic_id === '') {
      visitsPayload.company_id = '';
    }
    visitsPayload.sort_column = 'visit_date';
    visitsPayload.sort_direction = 'desc';
    visitsPayload.workcomp_employer_physicals_only = true;
    visitsPayload.limit = 5;
    setVisitsLoading(true);
    VisitsService.getAllVisits(visitsPayload)
      .then((visitsResponse) => {
        if (visitsResponse && visitsResponse.visits) {
          setDashBoardData((prev) => ({
            ...prev,
            visits: visitsResponse.visits
          }));
        }
        setVisitsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching visits:', error);
        setDashBoardData((prev) => ({
          ...prev,
          visits: []
        }));
        setVisitsLoading(false);
      });
    
    // Fetch open invoices data independently
    VisitsService.getOpenInvoices(payload)
      .then((invoicesResponse) => {
        if (invoicesResponse) {
          setDashBoardData((prev) => ({
            ...prev,
            open_invoices: invoicesResponse
          }));
        }
      })
      .catch((error) => {
        console.error('Error fetching open invoices:', error);
        setDashBoardData((prev) => ({
          ...prev,
          open_invoices: { current: 0, '30_plus_days': 0, '60_plus_days': 0, list: [] }
        }));
      });
  };

  // Refetch work restrictions when expanding/collapsing or sort changes
  const refetchWorkRestrictions = async (isFullscreen, patientName = '', sortColumn = null, sortDirection = null) => {
    setWorkRestrictionsLoading(true);
    props.Loader(true);
    // Start with global filters (override everything)
    let payload = {};
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      payload = { ...dashboardFilters };
      
      // Preserve date filters from local if not in global filters
      if (!dashboardFilters.from_date && dateFilter.from_date) {
        payload.from_date = dateFilter.from_date;
      }
      if (!dashboardFilters.to_date && dateFilter.to_date) {
        payload.to_date = dateFilter.to_date;
      }
    } else {
      // No global filters, use local filters
      if (filter) {
        payload = {
          ...filter,
          from_date: dateFilter.from_date,
          to_date: dateFilter.to_date,
        };
      }
    }

    // Handle clinic_id/company_id relationship
    if (payload.clinic_id === "") {
      payload.company_id = "";
    }

    payload.sort_column = sortColumn || 'visit_date';
    payload.sort_direction = sortDirection || 'desc';

    if (!isFullscreen) {
      payload.limit = 5; // Limit to 5 when not fullscreen
      payload.work_restrictions_fullscreen = false;
    } else {
      payload.work_restrictions_fullscreen = true;
      if (patientName && patientName.trim()) {
        payload.patient_name = patientName.trim();
      }
    }

    try {
      const response = await VisitsService.getWorkRestrictions(payload);
      if (response && response.work_restrictions) {
        setDashBoardData((prev) => ({
          ...prev,
          work_restrictions: response.work_restrictions
        }));
      }
    } catch (error) {
      console.error('Error fetching work restrictions:', error);
      setDashBoardData((prev) => ({
        ...prev,
        work_restrictions: []
      }));
    } finally {
      setWorkRestrictionsLoading(false);
      props.Loader(false);
    }
  };

  // Refetch employee compliance when expanding/collapsing or sort changes
  const refetchEmployeeCompliance = async (isFullscreen, patientName = '', sortColumn = null, sortDirection = null) => {
    setEmployeeComplianceLoading(true);
    props.Loader(true);
    // Start with global filters (override everything)
    let payload = {};
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      payload = { ...dashboardFilters };
      
      // Preserve date filters from local if not in global filters
      if (!dashboardFilters.from_date && dateFilter.from_date) {
        payload.from_date = dateFilter.from_date;
      }
      if (!dashboardFilters.to_date && dateFilter.to_date) {
        payload.to_date = dateFilter.to_date;
      }
    } else {
      // No global filters, use local filters
      if (filter) {
        payload = {
          ...filter,
          from_date: dateFilter.from_date,
          to_date: dateFilter.to_date,
        };
      }
    }

    // Handle clinic_id/company_id relationship
    if (payload.clinic_id === "") {
      payload.company_id = "";
    }

    payload.sort_column = sortColumn || 'due_date';
    payload.sort_direction = sortDirection || 'desc';

    if (!isFullscreen) {
      payload.limit = 5; // Limit to 5 when not fullscreen
      payload.compliance_fullscreen = false;
    } else {
      payload.compliance_fullscreen = true;
      if (patientName && patientName.trim()) {
        payload.patient_name = patientName.trim();
      }
    }

    try {
      const response = await VisitsService.getEmployeeComplianceMonitor(payload);
      if (response && response.employee_compliance) {
        setDashBoardData((prev) => ({
          ...prev,
          employee_compliance: response.employee_compliance
        }));
      }
    } catch (error) {
      console.error('Error fetching employee compliance monitor:', error);
      setDashBoardData((prev) => ({
        ...prev,
        employee_compliance: []
      }));
    } finally {
      setEmployeeComplianceLoading(false);
      props.Loader(false);
    }
  };

  // Refetch visits when expanding/collapsing or changing tabs
  const refetchVisits = async (isFullscreen, visitStatusFilter = null, patientName = '', sortColumn = null, sortDirection = null) => {
    setVisitsLoading(true);
    props.Loader(true);
    
    // Start with global filters (override everything)
    let visitsPayload = {};
    if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
      visitsPayload = { ...dashboardFilters };
    } else {
      // No global filters, use local filters (without date constraints)
      if (filter) {
        visitsPayload = {
          ...filter,
        };
      }
    }

    // Handle clinic_id/company_id relationship
    if (visitsPayload.clinic_id === "") {
      visitsPayload.company_id = "";
    }

    // Add visit status filter if provided (from tab selection)
    if (visitStatusFilter && visitStatusFilter !== 'All') {
      visitsPayload.visit_status_filter = visitStatusFilter;
    }

    // Add sort params for both short and fullscreen views
    visitsPayload.sort_column = sortColumn || 'visit_date';
    visitsPayload.sort_direction = sortDirection || 'desc';

    // Employer dashboard requirement:
    // Only include Work Comp and Employer Services Physicals visits.
    visitsPayload.workcomp_employer_physicals_only = true;

    // Add limit only when not fullscreen
    if (!isFullscreen) {
      visitsPayload.limit = 5;
    } else {
      // Remove limit when fullscreen to get all data
      delete visitsPayload.limit;
      visitsPayload.visits_fullscreen = true;
      // Add patient_name to payload when in fullscreen and provided
      if (patientName && patientName.trim()) {
        visitsPayload.patient_name = patientName.trim();
      }
    }

    try {
      const visitsResponse = await VisitsService.getAllVisits(visitsPayload);
      if (visitsResponse && visitsResponse.visits) {
        setDashBoardData((prev) => ({
          ...prev,
          visits: visitsResponse.visits
        }));
      }
    } catch (error) {
      console.error('Error fetching visits:', error);
      setDashBoardData((prev) => ({
        ...prev,
        visits: []
      }));
    } finally {
      setVisitsLoading(false);
      props.Loader(false);
    }
  };
  
  const setFilterDataEmpty = () => {
    setDateFilter({ from_date: "", to_date: "" });
  const updatedFilter = {
    ...filter,
      last_updated_by: "",
      clinic_id: "",
      company_id: "",
  };
    fetchDashBoard(updatedFilter, true);
  //fetchClinic();
  setCompanyOption([{ label: "All Company", value: "" }]);
  setFilter(updatedFilter);
  };
useEffect(() => {
    console.log("Updated dateFilter:", dateFilter);
}, [dateFilter]);
  const fetchCompany = async () => {
    let clinic_id = filter?.clinic_id;
    if (!clinic_id) return false;
    const response =
      await DropDownService.getAssignCompanyOptionsForSelfRegister(clinic_id);
    let companiesOptions = [{ label: "All Company", value: "" }];
    response.forEach((company) => {
      companiesOptions.push({
        label: company.name,
        value: company.company_uuid,
      });
    });
    setCompanyOption(companiesOptions);
  };
  const fetchClinic = async () => {
    let opt = [{ label: "All Clinic", value: "" }];
    let clinicResponse = await DropDownService.getClinicList(null);
    if (clinicResponse) {
      clinicResponse?.forEach((itm, ind) => {
        opt.push({ label: itm.name, value: itm.clinic_id });
      });
    }
    setClinicOptions(opt);
  };
  const filterChangeHandler = (val, name) => {
    const updatedFilter = { ...filter };
    if (val == "" && name == "clinic_id") {
      setCompanyOption([{ label: "All Company", value: "" }]);
    }
    if (name == "clinic_id" && val) {
      updatedFilter["company_id"] = "";
    }
    if (name == "last_updated_by") {
      setDateFilter({});
      closeDateFilterdropdown();
    }
    updatedFilter[name] = val;

    fetchDashBoard(updatedFilter);
    setFilter(updatedFilter);
  };
  const applyDateFilter = () => {
    const updatedFilter = {
      ...filter,
      last_updated_by: "",
      from_date: dateFilter.from_date,
      to_date: dateFilter.to_date,
  };
  fetchDashBoard(updatedFilter);
    setFilter({ ...filter, ["last_updated_by"]: "" });
  closeDateFilterdropdown();
  };
  const closeDateFilterdropdown = () => {
    if (filterDropdownRef.current) {
      filterDropdownRef.current.classList.toggle("show");
    }
  };
  const getColumnClass = () => {
    if (!hasVisitPermission) {
      return "col-lg-12";
    }
    return "col-lg-6";
};

  // GlobalSearch filter paths
  const globalSearchFilter = [
    "/visits",
    "/appointments",
    "/white-board",
    "/labs",
    "/result/vaccine",
    "/result/physical-drug-screens",
    "/employee",
    "/dashboard",
  ];

  // Get pathname using useLocation hook
  const location = useLocation();
  const { pathname } = location;
  const { profile } = props;

  // Get username from profile and convert to PascalCase
  const getUsername = () => {
    let name = '';
    if (profile) {
      if (profile.hasOwnProperty('name')) {
        name = profile.name;
      } else {
        name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      }
    }
    
    // Convert to PascalCase (first letter of each word capitalized, no spaces)
    if (name) {
      // Handle already formatted names or single words
      const words = name.trim().split(/\s+/).filter(word => word.length > 0);
      if (words.length === 0) return '';
      
      return words
        .map((word) => {
          // Capitalize first letter, lowercase the rest
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
    }
    return '';
  };

  const username = getUsername();

  return (
    <>
      <div className=" h-100">
        <div className="sh_cardBox border-0">
          <div
            className="sh_cardHeader border-0"
            style={{ position: "relative", padding: '1rem 1rem', minHeight: '80px' }}
          >
            {/* Three-column layout: Left (Title), Center (GlobalSearch), Right (Filters) */}
            <div className="d-flex position-relative">
              {/* LEFT SIDE: Title */}
              <h2 className="h2-strong position-absolute sh_welcome-text ">Welcome {username ? `${username}!` : '!'}</h2>
              
              {/* CENTER: Global Search - Flex centered */}
              <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
                {profile &&
                  profile.is_multiple_organization === false &&
                  profile.employee_login === false &&
                  globalSearchFilter.some((path) =>
                    pathname.startsWith(path)
                  ) && (
                    <GlobalSearch
                      activeMenu={"Appointments"}
                      pathname={pathname}
                    />
                  )}
              </div>

              {/* RIGHT SIDE: Filters */}
              <div className="d-flex justify-content-end align-items-center flex-wrap">
             <Acl acl={PERMISSIONS.CLINIC.LIST}>
              <div class="form-group dash_btn mx-1 mb-1 mx-1">
                <FormInputs
                  type="SelectList"
                  options={clinicOptions}
                  changeHandler={(value, e) =>
                        filterChangeHandler(value, "clinic_id", e)
                  }
                  value={filter?.clinic_id}
                      name={"clinic_id"}
                />
              </div>
              <div class="form-group dash_btn mx-1 mb-1">
                <FormInputs
                  type="SelectList"
                  options={companyOptions}
                  changeHandler={(value, e) =>
                        filterChangeHandler(value, "company_id", e)
                  }
                  value={filter?.company_id}
                      name={"company_id"}
                />
              </div>
              </Acl>
              </div>
            </div>
          </div>
          
          <div className="sh_cardBody sh_cardbglight pt-3 pb-4 px-4 overflow-auto">
            <DashboardCounts 
              dashboardData={dashboardCounts} 
              onPhysicalsClick={() => setPhysicalsFullscreenOpen(true)}
              onDrugScreenClick={() => setDrugScreenFullscreenOpen(true)}
              onVaccinesClick={() => setVaccinesFullscreenOpen(true)}
              onMissingClaimNumbersClick={() => setMissingClaimNumbersFullscreenOpen(true)}
            />
            <div className="row">
              <WorkRestrictions 
                data={dashboardData?.work_restrictions || []} 
                isLoading={workRestrictionsLoading} 
                onActionClick={(row, action) => console.log('Work Restrictions action:', action, row)}
                onExpand={refetchWorkRestrictions}
                Loader={props.Loader}
                Toast={props.Toast}
              />
              <Visits 
                data={dashboardData?.visits || []} 
                isLoading={visitsLoading} 
                onActionClick={(row, action) => console.log('Visits action:', action, row)}
                onExpand={refetchVisits}
                profile={props.profile}
                Loader={props.Loader}
                Toast={props.Toast}
              />
            </div>
            <div className="row">
              <EmployeeComplianceMonitor 
                data={dashboardData?.employee_compliance || []} 
                isLoading={employeeComplianceLoading} 
                onActionClick={(row, action) => console.log('Employee Compliance action:', action, row)}
                onExpand={refetchEmployeeCompliance}
              />
              <OpenInvoices 
                data={dashboardData?.open_invoices || { current: 0, '30_plus_days': 0, '60_plus_days': 0, list: [] }} 
                onActionClick={(row, action) => console.log('Open Invoices action:', action, row)} 
              />
            </div>
          </div>

        </div>
      </div>
      
      {/* Physicals Fullscreen Modal */}
      <DashboardCountsFullScreen
        category="physicals"
        isOpen={physicalsFullscreenOpen}
        onClose={() => setPhysicalsFullscreenOpen(false)}
        filters={dashboardFilters}
        Loader={props.Loader}
        Toast={props.Toast}
      />
      
      {/* Drug Screen Fullscreen Modal */}
      <DashboardCountsFullScreen
        category="drug_screen"
        isOpen={drugScreenFullscreenOpen}
        onClose={() => setDrugScreenFullscreenOpen(false)}
        filters={dashboardFilters}
        Loader={props.Loader}
        Toast={props.Toast}
      />
      
      {/* Vaccines Fullscreen Modal */}
      <DashboardCountsFullScreen
        category="vaccines"
        isOpen={vaccinesFullscreenOpen}
        onClose={() => setVaccinesFullscreenOpen(false)}
        filters={dashboardFilters}
        Loader={props.Loader}
        Toast={props.Toast}
      />
      
      {/* Missing Claim Numbers Fullscreen Modal */}
      <MissingClaimNumbersFullScreen
        isOpen={missingClaimNumbersFullscreenOpen}
        onClose={() => setMissingClaimNumbersFullscreenOpen(false)}
        filters={dashboardFilters}
        Loader={props.Loader}
        Toast={props.Toast}
        onIncidentDeleted={fetchDashBoard}
      />
    </>
  );
  function getLastUpdatedByOption() {
    return [
      { label: "Last 5 Minutes", value: "last_5_minutes" },
      { label: "Last 10 Minutes", value: "last_10_minutes" },
      { label: "Last 15 Minutes", value: "last_15_minutes" },
      { label: "Last 30 Minutes", value: "last_30_minutes" },
      { label: "Last 1 Hour", value: "last_1_hour" },
      { label: "Last 3 Hours", value: "last_2_hours" },
      { label: "Last 6 Hours", value: "last_6_hours" },
      { label: "Last 12 Hours", value: "last_12_hours" },
      { label: "Last 24 Hours", value: "last_24_hours" },
      { label: "Last 2 Days", value: "last_2_days" },
      { label: "Last 7 Days", value: "last_7_days" },
      { label: "Last 30 Days", value: "last_30_days" },
      { label: "Last 90 Days", value: "last_90_days" },
      { label: "Last 6 Months", value: "last_6_months" },
      { label: "Last 1 Year", value: "last_1_year" },
      { label: "Last 2 Years", value: "last_2_years" },
      { label: "Last 5 Years", value: "last_5_years" },
      { label: "Today", value: "today" },
      { label: "Yesterday", value: "yesterday" },
      { label: "Day Before Yesterday", value: "day_before_yesterday" },
      { label: "This day last week", value: "this_day_last_week" },
      { label: "Previous Week", value: "previous_week" },
      { label: "Previous Month", value: "previous_month" },
      { label: "This Week ", value: "this_week" },
      { label: "This Month ", value: "this_month" },
      { label: "This Year", value: "this_year" },
      { label: "ALL", value: "" },
    ];
  }
};

export default withReduxState(WithRoutify(DashboardContainer));